import type { Currency, EstimationParams, ModelInfo } from '@tcc/shared';
import { DEFAULT_PARAMS } from '@tcc/shared';
import { useEffect, useState } from 'react';
import { type AppConfig, getConfig } from './api/client';
import { Config } from './components/ConfigTab/Config';
import { isUnlocked, PasswordGate } from './components/PasswordGate/PasswordGate';
import { TabButton, type TabId } from './components/TabButton/TabButton';
import { Wizard } from './components/WizardTab/Wizard';

/**
 * Root application: password gate → header → Calculator / Configure tab switch.
 */
export function App() {
	const [unlocked, setUnlocked] = useState(isUnlocked);
	const [tab, setTab] = useState<TabId>('calc');
	const [uiVersion, setUiVersion] = useState<'classic' | 'clean'>('clean');
	const [currency, setCurrency] = useState<Currency>('GBP');
	const [liveEstimate, setLiveEstimate] = useState<{ monthly: number; currency: Currency } | null>(null);
	const [models, setModels] = useState<ModelInfo[]>([]);
	const [params, setParams] = useState<EstimationParams>(DEFAULT_PARAMS);
	const [defaults, setDefaults] = useState<AppConfig | null>(null);
	const [configStatus, setConfigStatus] = useState<'loading' | 'ready' | 'error'>('loading');
	const [retryCount, setRetryCount] = useState(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: retryCount is an intentional re-run trigger (incremented by the Retry button), not a value read inside the effect
	useEffect(() => {
		if (!unlocked) return;
		setConfigStatus('loading');
		getConfig()
			.then((c) => {
				setModels(c.models);
				setParams(c.params);
				setDefaults(c);
				setConfigStatus('ready');
			})
			.catch(() => setConfigStatus('error'));
	}, [unlocked, retryCount]);

	if (!unlocked) {
		return <PasswordGate onUnlock={() => setUnlocked(true)} />;
	}

	return (
		<>
			<a href="#main-content" className="skip-link">
				Skip to main content
			</a>

			{/* Top app bar: brand (left) · Calculator/Configuration tabs (centre) · currency + always-on estimate (right). */}
			<header className="appbar">
				<div className="appbar-brand">
					<h1>Lloyds Banking Group</h1>
					<span className="appbar-sub">AI cost estimator</span>
				</div>
				<div className="appbar-tabs" role="tablist" aria-label="View selection">
					<TabButton id="calc" label="Calculator" active={tab === 'calc'} onClick={setTab} />
					<TabButton id="config" label="Configuration" active={tab === 'config'} onClick={setTab} />
				</div>
				<div className="appbar-right">
					{/* biome-ignore lint/a11y/useSemanticElements: toggle buttons grouped by role="group" — <fieldset> is inappropriate here as these are not form inputs */}
					<div className="topccy" role="group" aria-label="Output currency">
						{(['GBP', 'USD'] as Currency[]).map((c) => (
							<button
								key={c}
								type="button"
								className={`topccy-btn${currency === c ? ' on' : ''}`}
								aria-pressed={currency === c}
								onClick={() => setCurrency(c)}
							>
								{c === 'GBP' ? '£' : '$'}
							</button>
						))}
					</div>
					<span className="estpill" aria-live="polite" title="Estimated monthly cost so far">
						<span className="estpill-k">EST</span>{' '}
						{liveEstimate
							? `${liveEstimate.currency === 'GBP' ? '£' : '$'}${Math.round(liveEstimate.monthly).toLocaleString()}/mo`
							: '—'}
					</span>
				</div>
			</header>

			<main id="main-content" tabIndex={-1}>
				{configStatus === 'loading' && (
					<div role="status" className="config-loading">
						Loading configuration…
					</div>
				)}

				{configStatus === 'error' && (
					<div className="config-error">
						<p>Could not load configuration. Check your connection and try again.</p>
						<button type="button" className="btnp" onClick={() => setRetryCount((n) => n + 1)}>
							Retry
						</button>
					</div>
				)}

				{configStatus === 'ready' && (
					<>
						{/* Both panels stay mounted and are toggled with `hidden` (rather than a
                ternary) so switching tabs doesn't reset in-progress wizard state. */}
						<div
							role="tabpanel"
							id="calc-panel"
							aria-labelledby="calc-tab"
							// biome-ignore lint/a11y/noNoninteractiveTabindex: ARIA tabpanel pattern requires tabIndex=0 so keyboard users can focus the panel content.
							tabIndex={0}
							hidden={tab !== 'calc'}
							style={{ outline: 'none' }}
						>
							<Wizard
								models={models}
								params={params}
								mode="easy"
								currency={currency}
								uiVersion={uiVersion}
								onLiveEstimate={setLiveEstimate}
							/>
						</div>
						<div
							role="tabpanel"
							id="config-panel"
							aria-labelledby="config-tab"
							// biome-ignore lint/a11y/noNoninteractiveTabindex: ARIA tabpanel pattern requires tabIndex=0 so keyboard users can focus the panel content.
							tabIndex={0}
							hidden={tab !== 'config'}
							style={{ outline: 'none' }}
						>
							<Config
								models={models}
								params={params}
								onModelsChange={setModels}
								onParamsChange={setParams}
								onReset={() => {
									if (defaults) {
										setModels(defaults.models);
										setParams(defaults.params);
									}
								}}
							/>
						</div>

						<div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
							{tab === 'calc' ? 'Calculator view' : 'Configuration view'}
						</div>
					</>
				)}
			</main>

			<footer className="appfoot">
				<span>Internal estimating tool — figures are indicative and not a quote.</span>
				<button
					type="button"
					className="uiswitch"
					onClick={() => setUiVersion((v) => (v === 'clean' ? 'classic' : 'clean'))}
				>
					{uiVersion === 'clean' ? 'Switch to classic layout' : 'Switch to new layout'}
				</button>
			</footer>
		</>
	);
}
